/**
 * GraphQL queries and hooks — dataObject pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import type { DataObject, DataObjectConnection, DataObjectFilter } from '../types/dataObject';

export const DATA_OBJECT_FIELDS = gql`
  fragment DataObjectFields on DataObject {
    entityId
    entityType
    name
    description
    domainId
    objectType
    classification
    containsPII
    containsPCI
    containsPHI
    encryptionRequired
    maskingRequired
    systemOfRecord
    isGoldenRecord
    lifecycleStatus
    qualityScore
    retentionPeriodDays
    tags
    notes
    documentationUrl
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
  }
`;

export const GET_DATA_OBJECT = gql`
  ${DATA_OBJECT_FIELDS}
  query GetDataObject($id: ID!) {
    dataObject(id: $id) {
      ...DataObjectFields
    }
  }
`;

// Note: the `dataObjectsByDomain` field takes only `domainId`, `first`, and
// `after` per schema.graphql — it has no `filter` argument. A `filter: $filter`
// argument here (even with the variable always undefined) fails GraphQL
// validation server-side, which starves the embedded Data Objects list on the
// DataDomain detail page silently — see issue #1306.
export const LIST_DATA_OBJECTS_BY_DOMAIN = gql`
  ${DATA_OBJECT_FIELDS}
  query ListDataObjectsByDomain($domainId: ID!, $first: Int, $after: String) {
    dataObjectsByDomain(domainId: $domainId, first: $first, after: $after) {
      edges {
        node {
          ...DataObjectFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export const LIST_DATA_OBJECTS = gql`
  ${DATA_OBJECT_FIELDS}
  query ListDataObjects($filter: DataObjectFilter, $first: Int, $after: String) {
    dataObjects(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          ...DataObjectFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export function useDataObject(id: string): QueryResult<{ dataObject: DataObject | null }> {
  return useQuery(GET_DATA_OBJECT, {
    variables: { id },
    skip: !id,
  });
}

export function useDataObjectsByDomain(
  domainId: string,
  first: number = 50
): QueryResult<{ dataObjectsByDomain: DataObjectConnection }> {
  return useQuery(LIST_DATA_OBJECTS_BY_DOMAIN, {
    variables: { domainId, first },
    skip: !domainId,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

export function useDataObjects(
  filter?: DataObjectFilter,
  first: number = 50
): QueryResult<{ dataObjects: DataObjectConnection }> {
  return useQuery(LIST_DATA_OBJECTS, {
    variables: { filter, first },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}
